import { FetchResult } from 'src/types/TApiQueries';
import {
  DeliveryAnalyticsFilters,
  PopularAssetsResponse,
  AudienceResponse,
  AudienceDimension,
  TimeseriesResponse,
  CachePerformanceResponse,
  PopularTransformsResponse,
  TransformGroupBy,
  AssetTransformsResponse,
  TransformCoverageResponse,
  CampaignsResponse,
  CampaignDetailResponse,
  ChannelsResponse,
  ChannelDetailResponse,
  DistinctValuesResponse,
  DistinctDimension,
} from 'src/types/TDeliveryAnalytics';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

const baseUrl = config.hostUrl;
const root = `${baseUrl}/api/v1/analytics/cdn`;

/** Append the common campaign/channel + time filters (snake_case) to a param bag. */
function applyFilters(params: URLSearchParams, f?: DeliveryAnalyticsFilters) {
  if (!f) return;
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (f.campaignKey) params.set('campaign_key', f.campaignKey);
  if (f.channelKey) params.set('channel_key', f.channelKey);
  if (f.placementKey) params.set('placement_key', f.placementKey);
  if (f.variantKey) params.set('variant_key', f.variantKey);
  if (f.audienceKey) params.set('audience_key', f.audienceKey);
  if (f.limit !== undefined) params.set('limit', String(f.limit));
}

function url(endpoint: string, params: URLSearchParams): string {
  const qs = params.toString();
  return qs ? `${root}/${endpoint}?${qs}` : `${root}/${endpoint}`;
}

function get<T>(target: string, accessToken: string): Promise<FetchResult<T>> {
  return performFetchWithLogging(target, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getPopularAssets(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters & { metric?: 'requests' | 'bytes' },
): Promise<FetchResult<PopularAssetsResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  if (opts?.metric) params.set('metric', opts.metric);
  return get(url('popular-assets', params), accessToken);
}

export async function getAudience(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters & { dimension?: AudienceDimension },
): Promise<FetchResult<AudienceResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  if (opts?.dimension) params.set('dimension', opts.dimension);
  return get(url('audience', params), accessToken);
}

export async function getTimeseries(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters & { interval?: 'hour' | 'day' },
): Promise<FetchResult<TimeseriesResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  if (opts?.interval) params.set('interval', opts.interval);
  return get(url('timeseries', params), accessToken);
}

export async function getCachePerformance(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<CachePerformanceResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  return get(url('cache-performance', params), accessToken);
}

export async function getPopularTransforms(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters & { groupBy?: TransformGroupBy },
): Promise<FetchResult<PopularTransformsResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  if (opts?.groupBy) params.set('group_by', opts.groupBy);
  return get(url('popular-transforms', params), accessToken);
}

export async function getAssetTransforms(
  accessToken: string,
  assetId: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<AssetTransformsResponse>> {
  const params = new URLSearchParams();
  params.set('asset_id', assetId);
  applyFilters(params, opts);
  return get(url('asset-transforms', params), accessToken);
}

export async function getTransformCoverage(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<TransformCoverageResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  return get(url('transform-coverage', params), accessToken);
}

export async function getCampaigns(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<CampaignsResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  return get(url('campaigns', params), accessToken);
}

export async function getCampaignDetail(
  accessToken: string,
  campaignKey: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<CampaignDetailResponse>> {
  const params = new URLSearchParams();
  params.set('campaign_key', campaignKey);
  applyFilters(params, opts);
  return get(url('campaign-detail', params), accessToken);
}

export async function getChannels(
  accessToken: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<ChannelsResponse>> {
  const params = new URLSearchParams();
  applyFilters(params, opts);
  return get(url('channels', params), accessToken);
}

export async function getChannelDetail(
  accessToken: string,
  channelKey: string,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<ChannelDetailResponse>> {
  const params = new URLSearchParams();
  params.set('channel_key', channelKey);
  applyFilters(params, opts);
  return get(url('channel-detail', params), accessToken);
}

export async function getDistinctValues(
  accessToken: string,
  dimension: DistinctDimension,
  opts?: DeliveryAnalyticsFilters,
): Promise<FetchResult<DistinctValuesResponse>> {
  const params = new URLSearchParams();
  params.set('dimension', dimension);
  applyFilters(params, opts);
  return get(url('distinct-values', params), accessToken);
}
